/* eslint-disable react-hooks/exhaustive-deps */
// App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  PublicClientApplication,
  InteractionStatus,
} from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { NotificationProvider } from "./contexts/NotificationContext";
// import {appInsights} from "./lib/utils/telemetryService"
// Import custom animations CSS for global access
import "./styles/custom-animations.css";

import Home from "./pages/Home";
import Auth from "./pages/Auth";
import ErrorPage from "./pages/ErrorPage";
import ChatSession from "./pages/ChatSession";
import MainContainer from "./components/ui/MainContainer";
import FullPageLoader from "./lib/common/components/loader/FullPageLoader";
import GenerateImage from "./pages/GenerateImage";
import GenerateVideo from "./pages/GenerateVideo";
import GenerateAudio from "./pages/GenerateAudio";
import GeneratePresentation from "./pages/GeneratePresentation";
import Cookies from "js-cookie";
import PropTypes from 'prop-types';
import DeepSearch from "./pages/DeepSearch";
import { trackEvent, trackPageView } from "./lib/utils/telemetryService";
import useSilentTokenRefresh from "./hooks/useSilentTokenRefresh";
import WeatherForecast from "./pages/WeatherForecast";
import ProjectView from "./pages/ProjectView";
import ProjectSession from "./pages/ProjectSession";
import GenerateMarketing from "./pages/GenerateMarketing";

import MeetingTranscript from "./pages/MeetingTranscript";

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_AD_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_AD_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

function MsalInitializer({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      trackEvent("MSAL_Initialized");
      setReady(true);
    });
  }, []);

  return ready ? <MsalProvider instance={msalInstance}>{children}</MsalProvider> : <FullPageLoader />;
}

MsalInitializer.propTypes = {
  children: PropTypes.node.isRequired,
};

// 🔐 AuthChecker for route protection + silent refresh
function AuthChecker({ children }) {
  useSilentTokenRefresh();
  const { instance, inProgress } = useMsal();
  const location = useLocation();
  const router = useNavigate();

  const isAuthPage = location.pathname.startsWith("/auth");
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const account = Cookies.get("idToken");

      if (!account && !isAuthPage) {
        trackEvent("Auth_Redirect_To_Login", { from: location.pathname });
        router("/auth");
      } else if (account && isAuthPage) {
        trackEvent("Auth_Redirect_To_Home");
        router("/");
      }

      setIsChecking(false);
    } else {
      setIsChecking(true);
    }
  }, [inProgress, instance, isAuthPage, router, location]);

  if (isChecking || inProgress !== InteractionStatus.None) {
    return <FullPageLoader />;
  }
  const account = Cookies.get("idToken");
  if (!account && !isAuthPage) {
    return <FullPageLoader />;
  }
  if (account && isAuthPage) {
    return <FullPageLoader />;
  }

  return children;
}

AuthChecker.propTypes = {
  children: PropTypes.node.isRequired,
};

function AppRoutes() {

  const location = useLocation();

  useEffect(() => {
    // ✅ Track page view every time location changes
    trackPageView(location.pathname, window.location.href);
  }, [location])

  return (
    <Routes>
      <Route
        path="/"
        element={
          <AuthChecker>
            <MainContainer />
          </AuthChecker>
        }
      >
        <Route path="" element={<Home />} />
        <Route path="chat/:sessionId" element={<ChatSession />} />
        <Route path="/generate-image" element={<GenerateImage />} />
        <Route path="/generate-video" element={<GenerateVideo />} />
        <Route path="/generate-audio" element={<GenerateAudio />} />
        <Route path="/generate-presentation" element={<GeneratePresentation />} />
        <Route path="/weather-forecast" element={<WeatherForecast />} />
        <Route path="/deep-search" element={<DeepSearch />} />
        <Route path="/project/:name/:projectType/:id" element={<ProjectView />} />
        <Route path="/project/:name/:projectType/:projectId/:sessionId" element={<ProjectSession />} />
        <Route path="/project/:name/marketing/:projectId" element={<GenerateMarketing />} />
         <Route path="/meeting-transcript" element={<MeetingTranscript />} />
      </Route>
      <Route
        path="/auth"
        element={
          <AuthChecker>
            <Auth />
          </AuthChecker>
        }
      />
      <Route path="/auth/error" element={<ErrorPage />} />
    </Routes>
  );
}

function App() {
  return (
    <MsalInitializer>
      <NotificationProvider>
        <div className="font-geist-sans font-geist-mono">
          <Router>
            <AppRoutes />
          </Router>
        </div>
      </NotificationProvider>
    </MsalInitializer>
  );
}

export default App;
